import { PriceItemDto, VariantTreeDto } from "../VariantTreeDto";

export type StoreOffersGroup = {
  store: string;
  offers: PriceItemDto[];
  minPrice: number;
  currency: string;
};

export type StoreSummary = {
  store: string;
  offersCount: number;
  minPrice: number;
  currency: string;
};

export type ProductPriceSummary = {
  minPrice: number;
  currency: string;
  offersCount: number;
  storesCount: number;
};

const byPrice = (a: { minPrice: number }, b: { minPrice: number }) =>
  a.minPrice - b.minPrice;

export const groupOffersByStore = (
  offers: PriceItemDto[] | undefined,
): StoreOffersGroup[] => {
  const groups = new Map<string, StoreOffersGroup>();

  for (const offer of offers ?? []) {
    if (!offer.store) continue;

    const group = groups.get(offer.store);

    if (!group) {
      groups.set(offer.store, {
        store: offer.store,
        offers: [offer],
        minPrice: offer.price,
        currency: offer.currency,
      });
      continue;
    }

    group.offers.push(offer);
    group.minPrice = Math.min(group.minPrice, offer.price);
  }

  return [...groups.values()]
    .map((group) => ({
      ...group,
      offers: [...group.offers].sort((a, b) => a.price - b.price),
    }))
    .sort(byPrice);
};

const eachOffer = function* (variants: VariantTreeDto) {
  for (const variant of Object.values(variants.variants)) {
    for (const offer of variant.offers ?? []) {
      yield offer;
    }
  }
};

export const collectProductStores = (
  variants: VariantTreeDto | null,
): StoreSummary[] => {
  if (!variants) return [];

  const summaries = new Map<string, StoreSummary>();

  for (const offer of eachOffer(variants)) {
    if (!offer.store) continue;

    const summary = summaries.get(offer.store);

    if (!summary) {
      summaries.set(offer.store, {
        store: offer.store,
        offersCount: 1,
        minPrice: offer.price,
        currency: offer.currency,
      });
      continue;
    }

    summary.offersCount += 1;
    summary.minPrice = Math.min(summary.minPrice, offer.price);
  }

  return [...summaries.values()].sort(byPrice);
};

export const summarizeProductPrices = (
  groups: StoreOffersGroup[],
): ProductPriceSummary | null => {
  if (!groups.length) return null;

  const cheapest = groups[0];

  return {
    minPrice: cheapest.minPrice,
    currency: cheapest.currency,
    offersCount: groups.reduce(
      (total, group) => total + group.offers.length,
      0,
    ),
    storesCount: groups.length,
  };
};

export const getValuesWithoutStore = (
  variants: VariantTreeDto | null,
  store: string | null,
): Record<string, string[]> => {
  if (!variants || !store) return {};

  const availableValues = new Map<string, Set<string>>();

  for (const [path, variant] of Object.entries(variants.variants)) {
    const hasStore = variant.offers?.some((offer) => offer.store === store);

    if (!hasStore) continue;

    for (const pair of path.split("|")) {
      const separator = pair.indexOf("=");

      if (separator < 0) continue;

      const attributeName = pair.slice(0, separator);
      const value = pair.slice(separator + 1);

      const values = availableValues.get(attributeName) ?? new Set<string>();
      values.add(value);
      availableValues.set(attributeName, values);
    }
  }

  return Object.fromEntries(
    Object.entries(variants.attributes).map(([attributeName, values]) => [
      attributeName,
      values.filter((value) => !availableValues.get(attributeName)?.has(value)),
    ]),
  );
};
